/**
 * 掲示板 本文の AI 作文ダイアログ。
 * 仕様書: docs/02_work/done/plan_board_feature.md §7-6
 *
 * フロー:
 *   1. 生成本文タイプを選ぶ（承認依頼/決定事項確認・業務依頼・報告/まとめ）
 *   2. (任意) テキストエリアに素材をコピペ
 *   3. (任意) PDF・画像をドラッグ&ドロップ（無ければ AI は読まない）
 *   4. 生成 → 本文へ反映
 *
 * ★ 本文（編集中）か テキストエリア のどちらかは必須（両方可）。
 */
import { FileText, Image as ImageIcon, Sparkles, X } from 'lucide-react';
import { useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { useComposeBoardBody, type BoardComposeKind } from '@/hooks/useBoardAiCompose';

/** 添付できる形式（サーバ側の許可リストと一致させること）。 */
const ACCEPT = '.pdf,.png,.jpg,.jpeg,.gif,.webp';
const ALLOWED = ['application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp'];
const MAX_FILES = 5;
const MAX_MB = 4;
/** 合計上限（Callable 10MB 制限。base64 で約4/3に膨らむため）。 */
const MAX_TOTAL_MB = 6;

const KINDS: Array<{ value: BoardComposeKind; label: string; hint: string }> = [
  { value: 'approval', label: '承認依頼／決定事項確認', hint: '承認・決定してほしいことを整理' },
  { value: 'task', label: '業務依頼', hint: '依頼内容・手順・成果物を整理' },
  { value: 'report', label: '報告／まとめ', hint: '結論を先に、事実を箇条書きで' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  /** 編集中の本文（プレーンテキスト）。テキストエリアと合わせてどちらか必須。 */
  currentBodyText: string;
  /** 生成結果を本文へ反映する。 */
  onApply: (bodyText: string) => void;
}

export function AiComposeDialog({ open, onClose, currentBodyText, onApply }: Props) {
  const compose = useComposeBoardBody();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [kind, setKind] = useState<BoardComposeKind>('report');
  const [sourceText, setSourceText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);

  const busy = compose.isPending;
  const hasInput = currentBodyText.trim() !== '' || sourceText.trim() !== '';

  const addFiles = (list: FileList | null | undefined) => {
    if (!list) return;
    const incoming = Array.from(list);
    const next: File[] = [...files];
    for (const f of incoming) {
      if (!ALLOWED.includes(f.type)) {
        toast.error(`${f.name}: PDF または画像のみ添付できます`);
        continue;
      }
      if (f.size > MAX_MB * 1024 * 1024) {
        toast.error(`${f.name}: ${MAX_MB}MB 以下にしてください`);
        continue;
      }
      if (next.length >= MAX_FILES) {
        toast.error(`添付は ${MAX_FILES} 件までです`);
        break;
      }
      const total = next.reduce((sum, x) => sum + x.size, 0) + f.size;
      if (total > MAX_TOTAL_MB * 1024 * 1024) {
        toast.error(`添付は合計 ${MAX_TOTAL_MB}MB までです`);
        break;
      }
      next.push(f);
    }
    setFiles(next);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  /** File を base64（データURLのプレフィックス除去）に変換する。 */
  const toBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const s = String(reader.result ?? '');
        const comma = s.indexOf(',');
        resolve(comma >= 0 ? s.slice(comma + 1) : s);
      };
      reader.onerror = () => reject(new Error('ファイルの読み込みに失敗しました'));
      reader.readAsDataURL(file);
    });

  const handleGenerate = async () => {
    if (!hasInput) {
      toast.error('本文またはテキストのどちらかを入力してください');
      return;
    }
    try {
      const attachments = await Promise.all(
        files.map(async (f) => ({ mediaType: f.type, data: await toBase64(f) })),
      );
      const res = await compose.mutateAsync({
        kind,
        bodyText: currentBodyText,
        sourceText: sourceText.trim() || undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
      });
      onApply(res.bodyText);
      toast.success('本文を生成しました');
      // 次回のために入力はリセットする
      setSourceText('');
      setFiles([]);
      onClose();
    } catch (err) {
      toast.error(`生成失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // 生成中は ESC / 背景クリックでも閉じない
        if (!next && !busy) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Sparkles className="size-4 text-[var(--accent)]" />
            AI作文
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* 1. 生成本文タイプ */}
          <div className="space-y-1.5">
            <Label>生成する本文のタイプ</Label>
            <div className="space-y-1.5">
              {KINDS.map((k) => (
                <button
                  key={k.value}
                  type="button"
                  onClick={() => setKind(k.value)}
                  disabled={busy}
                  className={`w-full text-left rounded-md border p-2.5 transition-colors ${
                    kind === k.value
                      ? 'border-[var(--accent)] bg-[var(--bg-accent-subtle,transparent)]'
                      : 'border-[var(--border)]'
                  }`}
                >
                  <div className="text-sm text-[var(--text-primary)]">{k.label}</div>
                  <div className="text-[11px] text-[var(--text-muted)]">{k.hint}</div>
                </button>
              ))}
            </div>
          </div>

          {/* 2. テキスト素材（任意） */}
          <div className="space-y-1">
            <Label>
              テキスト<span className="text-[var(--text-muted)]">（任意）</span>
            </Label>
            <textarea
              value={sourceText}
              onChange={(e) => setSourceText(e.target.value)}
              disabled={busy}
              rows={5}
              placeholder="会話ログ・メモ・議事録などを貼り付けてください"
              className="w-full rounded-md border border-[var(--border)] bg-[var(--bg-input,transparent)] p-2 text-sm text-[var(--text-primary)]"
            />
            {!hasInput && (
              <p className="text-[11px] text-[var(--accent-amber)]">
                本文かテキストのどちらかを入力してください
              </p>
            )}
          </div>

          {/* 3. 添付（任意） */}
          <div className="space-y-1">
            <Label>
              ファイル添付<span className="text-[var(--text-muted)]">（任意・PDF / 画像）</span>
            </Label>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT}
              multiple
              className="hidden"
              onChange={(e) => addFiles(e.target.files)}
            />
            <div
              role="button"
              tabIndex={0}
              onClick={() => !busy && fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (!busy && (e.key === 'Enter' || e.key === ' ')) fileInputRef.current?.click();
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                if (!busy) addFiles(e.dataTransfer.files);
              }}
              className={`rounded-md border border-dashed p-3 text-center cursor-pointer transition-colors ${
                dragOver ? 'border-[var(--accent)]' : 'border-[var(--border)]'
              }`}
            >
              <div className="text-xs text-[var(--text-secondary)]">
                PDF・画像をドラッグ＆ドロップ
              </div>
              <div className="text-[11px] text-[var(--text-muted)]">
                クリックで選択／{MAX_FILES}件・1件{MAX_MB}MB・合計{MAX_TOTAL_MB}MBまで
              </div>
            </div>

            {files.length > 0 && (
              <div className="space-y-1 pt-1">
                {files.map((f, i) => (
                  <div
                    key={`${f.name}-${i}`}
                    className="flex items-center gap-2 rounded border border-[var(--border)] px-2 py-1"
                  >
                    {f.type === 'application/pdf' ? (
                      <FileText className="size-3.5 text-[var(--text-secondary)] flex-shrink-0" />
                    ) : (
                      <ImageIcon className="size-3.5 text-[var(--text-secondary)] flex-shrink-0" />
                    )}
                    <span className="flex-1 min-w-0 truncate text-xs text-[var(--text-primary)]">
                      {f.name}
                    </span>
                    {!busy && (
                      <button
                        type="button"
                        onClick={() => setFiles(files.filter((_, j) => j !== i))}
                        aria-label={`${f.name} を外す`}
                        className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                      >
                        <X className="size-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <p className="text-[11px] text-[var(--text-muted)]">
            挨拶文などの定型句は入れず、用件だけを生成します。生成後に本文を編集できます。
          </p>
        </div>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            閉じる
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => void handleGenerate()}
            disabled={busy || !hasInput}
          >
            {busy ? '生成中…' : '本文を生成'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
